














































import ChosenImage from "@/components/ChosenImage.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import QuestionChoice from "@/components/questions/QuestionChoice.vue";
import IAvatar from "@/typings/IAvatar";
import IConfig from "@/typings/IConfig";
import IQuestion from "@/typings/IQuestion";
import anime, { AnimeInstance } from "animejs";
import { Component, Inject, Vue, Watch } from "vue-property-decorator";

/**
 * The survey question view.
 */
@Component({
  components: {
    ChosenImage,
    ProgressBar,
    QuestionChoice
  }
})
export default class Survey extends Vue {
  @Inject("queryWorker") private queryWorker!: Worker;

  private chosenImageAnimation!: AnimeInstance;
  private questionAnimation!: AnimeInstance;
  private reviewTitleAnimation!: AnimeInstance;
  private titleAnimation!: AnimeInstance;

  private readonly duration = 2000;
  private readonly easing = "easeInOutCubic";

  // using a separate index to control when survey question components gets updated.
  private controlledQuestionIndex: number = 0;
  private hasSuccess: boolean = false;
  private isPending: boolean = false;
  private isReadyForReview: boolean = false;

  @Watch("isComplete")
  private onIsCompleteChanged(value: boolean, oldValue: boolean) {
    let questionAnimation: AnimeInstance;
    questionAnimation = anime({
      autoplay: false,
      complete: () => {
        this.isReadyForReview = !this.isReadyForReview;
      },
      duration: 600,
      easing: this.easing,
      height: [100, 0],
      opacity: [100, 0],
      targets: ".question"
    });
    if (value) {
      questionAnimation.play();
      if (!this.chosenImageAnimation.reversed) {
        this.chosenImageAnimation.reverse();
      }
      if (this.reviewTitleAnimation.reversed) {
        this.reviewTitleAnimation.direction = "normal";
      }
      this.reviewTitleAnimation.play();
      this.chosenImageAnimation.play();
    } else {
      questionAnimation.reverse();
      if (this.chosenImageAnimation.reversed) {
        this.chosenImageAnimation.direction = "normal";
      }
      if (!this.reviewTitleAnimation.reversed) {
        this.reviewTitleAnimation.reverse();
      }
      questionAnimation.play();
      this.chosenImageAnimation.play();
      this.reviewTitleAnimation.play();
    }
  }

  @Watch("currentQuestionIndex")
  private onCurrentQuestionIndexChanged(value: number, oldValue: number) {
    if (this.currentQuestionIndex >= this.deck.length) return;
    this.controlledQuestionIndex = value;
  }

  /**
   * The base app URI configured in env.
   */
  private get apiUri(): string | undefined {
    return process.env.VUE_APP_API_URI;
  }

  /**
   * The currently selected avatar.
   */
  private get avatar(): IAvatar | undefined {
    return this.$store.getters.avatar;
  }

  /**
   * The app configuration object.
   */
  private get CONFIG(): IConfig {
    return this.$store.getters.CONFIG;
  }

  private get currentQuestionIndex(): number {
    return this.$store.getters.currentQuestionIndex;
  }

  private get deck(): IQuestion[] {
    return this.$store.getters.deck;
  }

  /**
   * Returns true if all survey questions have been answered.
   */
  private get isComplete(): boolean {
    return this.currentQuestionIndex >= this.deck.length;
  }

  /**
   * The progress bar's current progress level.
   */
  private get progress(): number {
    return this.$store.getters.progress;
  }

  /**
   * The component that should be used for the current question based on its
   * format.
   */
  private get questionComponent(): string {
    if (!this.deck) return "";
    const question = this.deck[this.controlledQuestionIndex];
    if (!question) return "";
    return "QuestionChoice";
  }

  private handleBack(): void {
    this.$store.dispatch("undoResponse");
    this.$store.dispatch("decrementProgress");
  }

  /**
   * Handles the next event emitted by the Question component.
   */
  private handleNext(): void {
    window.scrollTo(0, 0);
    anime({
      autoplay: true,
      duration: 600,
      easing: "easeInOutCubic",
      opacity: [0, 1],
      targets: ".question"
    });
  }

  /**
   * Handles the survey submission.
   */
  private handleSubmit(): void {
    if (this.isPending) return;
    this.isPending = true;
    this.queryWorker.postMessage({
      avatarId: this.$store.getters.avatarId,
      image: this.$store.getters.image,
      responses: this.$store.getters.responses,
      type: "responses"
    });
    this.queryWorker.addEventListener("message", (event: MessageEvent) => {
      if (event.data.type !== "response") return;
      this.isPending = false;
      this.hasSuccess = true;
    });
    this.$router.push("complete");
  }

  /**
   * Start the interstitial exit animation sequence after a specified duration.
   */
  private interstitialTimeout(duration: number): void {
    setTimeout(() => {
      this.leaveInsterstitial();
    }, duration);
  }

  private leaveInsterstitial(): void {
    if (
      !this.chosenImageAnimation ||
      !this.questionAnimation ||
      !this.titleAnimation
    )
      return;
    this.chosenImageAnimation.play();
    this.questionAnimation.play();
    this.titleAnimation.play();
  }

  /**
   * Register various animations.
   */
  private registerAnimations(): void {
    this.chosenImageAnimation = anime({
      autoplay: false,
      duration: this.duration,
      easing: this.easing,
      targets: ".chosen-image",
      width: 80
    });
    this.questionAnimation = anime({
      autoplay: false,
      delay: this.duration,
      duration: this.duration,
      easing: this.easing,
      opacity: [0, 1],
      targets: [".progress", ".question"]
    });
    this.reviewTitleAnimation = anime({
      autoplay: false,
      duration: this.duration,
      easing: this.easing,
      height: [0, 100],
      marginTop: 16,
      opacity: 100,
      targets: ".review-title"
    });
    this.titleAnimation = anime({
      autoplay: false,
      duration: this.duration,
      easing: this.easing,
      height: 0,
      marginBottom: ["64", "16"],
      marginTop: [40, 0],
      opacity: 0,
      targets: ".interstitial-title"
    });
  }

  private beforeMount(): void {
    if (!this.CONFIG.SHUFFLE_DECK) return;
    this.$store.dispatch("shuffleDeck");
  }

  private mounted(): void {
    window.scrollTo(0, 0);
    this.registerAnimations();
    this.interstitialTimeout(2000);
  }
}
